import React, { useState, useEffect } from 'react';
import {
  Card, CardBody, CardHeader, ListGroup, ListGroupItem, Row, Col, Table, Badge
} from 'reactstrap';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import LightGallery from 'lightgallery/react';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import { formatAddress, Address } from '../../models/Address.interface';
import { Attachment } from '../../models/Attachment.interface';
import AttachmentGallery from './AttachmentGallery';

interface ViewJobBidProps {
  jobId: string;
  bidId: string;
}

const ViewJobBid: React.FC<ViewJobBidProps> = ({ jobId, bidId }) => {
  const [bidData, setBidData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBidData = async () => {
      const firestore = firebase.firestore();

      try {
        const bidDoc = await firestore.collection('jobs').doc(jobId).collection('bids').doc(bidId).get();

        if (bidDoc.exists) {
          setBidData(bidDoc.data());
        } else {
          console.error('Bid not found');
        }
      } catch (error) {
        console.error('Error fetching bid data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBidData();
  }, [jobId, bidId]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!bidData) {
    return <div>Bid not found</div>;
  }

  // Ensure costBreakdown is an array
  const costBreakdown = Array.isArray(bidData.costBreakdown) ? bidData.costBreakdown : [];

  // Ensure paymentSchedule is an array
  const paymentSchedule = Array.isArray(bidData.paymentSchedule) ? bidData.paymentSchedule : [];

  const imageAttachments = bidData.attachments?.filter((attachment: Attachment) =>
    /\.(jpg|jpeg|png|gif)/i.test(attachment.url)
  ) || [];

  const otherAttachments = bidData.attachments?.filter((attachment: Attachment) =>
    !/\.(jpg|jpeg|png|gif)/i.test(attachment.url)
  ) || [];



  return (
    <Card>
      <CardHeader>Bid Details</CardHeader>
      <CardBody>
        <ListGroup>
          <ListGroupItem>
            <strong>Contractor Name:</strong> {bidData.contractorName}
          </ListGroupItem>
          <ListGroupItem>
            <strong>Bid Type:</strong> {bidData.bidType === 'fixedPrice' ? 'Fixed Price' : 'Hourly'}
          </ListGroupItem>
          {bidData.bidType === 'fixedPrice' && (
            <ListGroupItem>
              <strong>Fixed Price Type:</strong> {bidData.fixedPriceType === 'laborAndMaterials' ? 'Labor and Materials' : 'Labor Only'}
            </ListGroupItem>
          )}
          <ListGroupItem>
            <strong>Bid Amount:</strong> ${bidData.bidAmount}
          </ListGroupItem>

          <ListGroupItem>
            <strong>Start Date:</strong> {bidData.startDate}
          </ListGroupItem>
          <ListGroupItem>
            <strong>End Date:</strong> {bidData.endDate}
          </ListGroupItem>
          <ListGroupItem>
            <strong>Approach:</strong> {bidData.approach}
          </ListGroupItem>
          <ListGroupItem>
            <strong>Contractor License:</strong> {bidData.contractorLicense}
          </ListGroupItem>
          <ListGroupItem>
            <strong>Contractor Address:</strong> {formatAddress(bidData.contractorAddress)}
          </ListGroupItem>
          <ListGroupItem>
            <strong>Contractor Phone:</strong> {bidData.contractorPhone}
          </ListGroupItem>
          <ListGroupItem>
            <strong>Contractor Email:</strong> {bidData.contractorEmail}
          </ListGroupItem>
          <ListGroupItem>
            <strong>Warranty Period:</strong> {bidData.warrantyPeriod} days
          </ListGroupItem>
          <ListGroupItem>
            <strong>Waive Inspection:</strong> <Badge color={bidData.requireInspection ? 'success' : 'danger'}>{bidData.requireInspection ? 'Yes' : 'No'}</Badge>
          </ListGroupItem>
          <ListGroupItem>
            <strong>Termination Notice Days:</strong> {bidData.terminationNoticeDays}
          </ListGroupItem>
          <ListGroupItem>
            <strong>Delay Days:</strong> {bidData.delayDays}
          </ListGroupItem>
          <ListGroupItem>
            <strong>Bid Date:</strong> {bidData.createdAt?.toDate().toLocaleString()}
          </ListGroupItem>
        </ListGroup>

        <h5 className="mt-4">Cost Breakdown</h5>
        <Table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Contractor Amount</th>
              <th>Homeowner Amount</th>
            </tr>
          </thead>
          <tbody>
            {costBreakdown?.map((item: any, index: number) => (
              <tr key={index}>
                <td>{item.description}</td>
                <td>${item.contractorAmount}</td>
                <td>${item.homeownerAmount}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <h5 className="mt-4">Payment Schedule</h5>
        <Table>
          <thead>
            <tr>
              <th>Condition</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            {paymentSchedule?.map((item: any, index: number) => (
              <tr key={index}>
                <td>{item.condition}</td>
                <td>{item.percentage}%</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Row className="mt-4">
          <Col md={12}>
            <h5>Image Attachments</h5>
            {imageAttachments.length > 0 ? (
              <AttachmentGallery
                attachments={imageAttachments || []}
              />
            ) : (
              <p>No image attachments available</p>
            )}
          </Col>
        </Row>

        <Row className="mt-4">
          <Col md={12}>
            <h5>Other Attachments</h5>
            {otherAttachments.map((attachment: Attachment, index: number) => (
              <ListGroupItem key={attachment.id || index}>
                <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                  {attachment.caption || decodeURIComponent(attachment.url.split('/').pop()?.split('?')[0] || '')}
                </a>
              </ListGroupItem>
            ))}
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default ViewJobBid;